import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import UserAvatar from './UserAvatar';
import { formatRelativeTime } from '../../utils/relativeTime';
import { fallbackColorForUid } from '../../utils/userColors';

/**
 * Real-time comments thread on a Firestore subcollection.
 * Shared by the Leads edit dialog and the Tasks dialog.
 *
 * Comments are written immediately when posted, independent of the parent
 * dialog's Save button. Author name/color are denormalized onto each comment
 * so threads survive user deletion; a live lookup via `usersByUid` wins when
 * available so color edits propagate.
 *
 * Props:
 * - pathSegments: e.g. ['leads', leadId, 'comments']
 * - currentUser: { uid, name, color }
 * - usersByUid: map of users keyed by their `uid` field ({ name, color })
 * - variant: 'lead' = avatar beside input, Post button below;
 *            'task' = no avatar, full-height POST button beside a resizable textarea
 * - onAfterPost / onAfterDelete: optional callbacks (Tasks maintains commentCount)
 */
const CommentsThread = ({
  pathSegments,
  currentUser,
  usersByUid = {},
  variant = 'lead',
  onAfterPost,
  onAfterDelete
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const listRef = useRef(null);
  const scrollPendingRef = useRef(false);

  useEffect(() => {
    const commentsQuery = query(
      collection(db, ...pathSegments),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      setComments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSegments.join('/')]);

  useEffect(() => {
    if (scrollPendingRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
      scrollPendingRef.current = false;
    }
  }, [comments]);

  const resolveAuthor = (comment) => {
    const live = usersByUid[comment.authorUid];
    return {
      name: live?.name || comment.authorName || 'Unknown user',
      color: live?.color || comment.authorColor || fallbackColorForUid(comment.authorUid)
    };
  };

  const handlePost = async () => {
    const body = newComment.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      await addDoc(collection(db, ...pathSegments), {
        authorUid: currentUser.uid,
        authorName: currentUser.name,
        authorColor: currentUser.color,
        body,
        createdAt: serverTimestamp()
      });
      setNewComment('');
      scrollPendingRef.current = true;
      if (onAfterPost) onAfterPost();
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(doc(db, ...pathSegments, commentId));
      if (onAfterDelete) onAfterDelete();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handlePost();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Comments
        </Typography>
        <Box
          sx={{
            bgcolor: 'action.hover',
            color: 'text.secondary',
            px: 1,
            py: 0.25,
            borderRadius: 3,
            fontSize: '0.75rem',
            fontWeight: 500,
            minWidth: 24,
            textAlign: 'center'
          }}
        >
          {comments.length}
        </Box>
      </Box>

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          py: 2,
          pr: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        {comments.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 4 }}
          >
            No comments yet. Be the first to add one.
          </Typography>
        ) : (
          comments.map((comment) => {
            const author = resolveAuthor(comment);
            const isOwn = comment.authorUid === currentUser.uid;
            return (
              <Box
                key={comment.id}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  '&:hover .comment-delete': { opacity: 1 }
                }}
              >
                <UserAvatar name={author.name} color={author.color} size={32} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                      minHeight: 20
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {author.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(comment.createdAt)}
                    </Typography>
                    {isOwn && (
                      <Box className="comment-delete" sx={{ ml: 'auto', opacity: 0, transition: 'opacity 120ms' }}>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(comment.id)}
                            sx={{
                              '&:hover': { color: 'error.main', bgcolor: 'rgba(211,47,47,0.08)' }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                  >
                    {comment.body}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        {variant === 'lead' ? (
          <>
            <UserAvatar name={currentUser.name} color={currentUser.color} size={32} />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                multiline
                minRows={1}
                maxRows={4}
                size="small"
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handlePost}
                  disabled={!newComment.trim() || posting}
                >
                  Post
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <TextField
              multiline
              placeholder="Add a comment…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': { alignItems: 'flex-start' },
                '& textarea': {
                  resize: 'vertical',
                  minHeight: 64,
                  maxHeight: 200
                }
              }}
            />
            <Button
              onClick={handlePost}
              disabled={!newComment.trim() || posting}
              sx={{
                alignSelf: 'stretch',
                minHeight: 64,
                px: 2.25,
                '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.04)' }
              }}
            >
              Post
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CommentsThread;
